import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Clock, MessageCircle } from 'lucide-react';
import { supabase } from '@/lib/supabase';
import { useAuth } from '@/contexts/AuthContext';
import { useI18n } from '@/i18n/LanguageContext';
import { whatsappHref } from '@/data/constants';

/**
 * A buyer opened the secure (hosted) $29 checkout for a lead, but no payment has
 * reached GigGone. Previously My Leads was simply empty — the exact confusion that
 * produced the 2026-10-01 support call. Now the buyer sees what happened, that they
 * must NOT pay again, and a one-tap WhatsApp message pre-filled with their email
 * and the lead. Admins are alerted server-side after 12 minutes.
 */
interface Session {
  id: string;
  listing_id: string | null;
  listing_slug: string | null;
  started_at: string;
}

const AwaitingPaymentNotice: React.FC<{ ownedListingIds: string[] }> = ({ ownedListingIds }) => {
  const { profile } = useAuth();
  const { lang, path } = useI18n();
  const es = lang === 'es';
  const [rows, setRows] = useState<Session[]>([]);
  const ownedKey = ownedListingIds.join(',');

  useEffect(() => {
    if (!profile?.id) return;
    let alive = true;
    const since = new Date(Date.now() - 48 * 3600 * 1000).toISOString();
    supabase
      .from('mkt_hosted_checkout_sessions')
      .select('id, listing_id, listing_slug, started_at')
      .eq('user_id', profile.id)
      .eq('plan_code', 'single')
      .in('status', ['started', 'returned', 'unmatched'])
      .gte('started_at', since)
      .order('started_at', { ascending: false })
      .then(({ data }) => {
        if (!alive) return;
        const owned = new Set(ownedKey ? ownedKey.split(',') : []);
        const seen = new Set<string>();
        setRows(
          ((data as Session[]) || []).filter((s) => {
            if (!s.listing_id || owned.has(s.listing_id) || seen.has(s.listing_id)) return false;
            seen.add(s.listing_id);
            return true;
          }),
        );
      });
    return () => {
      alive = false;
    };
  }, [profile?.id, ownedKey]);

  if (!rows.length) return null;

  return (
    <div className="mb-4 space-y-3">
      {rows.map((s) => {
        const when = new Date(s.started_at).toLocaleString(es ? 'es-US' : 'en-US', { dateStyle: 'medium', timeStyle: 'short' });
        const msg = es
          ? `Hola GIGGONE, pagué $29 por el lead ${s.listing_slug || ''} el ${when} y no aparece en Mis Leads. Mi correo de cuenta: ${profile?.email || ''}`
          : `Hi GIGGONE, I paid $29 for lead ${s.listing_slug || ''} on ${when} and it is not in My Leads. My account email: ${profile?.email || ''}`;
        return (
          <div key={s.id} className="rounded-2xl border border-amber-300 bg-amber-50 p-4">
            <p className="flex items-center gap-2 text-sm font-extrabold text-amber-900">
              <Clock className="h-4 w-4 shrink-0" />
              {es ? 'Aún no hemos recibido la confirmación de su pago' : 'We have not received your payment confirmation yet'}
            </p>
            <p className="mt-1 text-sm text-amber-900">
              {es
                ? `Abrió el pago seguro de $29 el ${when}. Si se le cobró, NO pague de nuevo — nuestro equipo ya fue alertado y agregará el lead aquí y se lo enviará por correo. Si no terminó de pagar, puede volver al proyecto.`
                : `You opened the secure $29 checkout on ${when}. If you were charged, do NOT pay again — our team has been alerted and will add the lead here and email it to you. If you did not finish paying, you can go back to the project.`}
            </p>
            <div className="mt-3 flex flex-col gap-2 sm:flex-row">
              <a
                href={`${whatsappHref(lang).split('?')[0]}?text=${encodeURIComponent(msg)}`}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex min-h-[44px] items-center justify-center gap-1.5 rounded-xl bg-emerald-600 px-4 text-sm font-bold text-white hover:bg-emerald-700"
              >
                <MessageCircle className="h-4 w-4" /> {es ? 'Ya pagué — avisar a soporte' : 'I paid — tell support'}
              </a>
              {s.listing_slug && (
                <Link
                  to={path(`/jobs/${s.listing_slug}`)}
                  className="inline-flex min-h-[44px] items-center justify-center rounded-xl border-2 border-slate-300 bg-white px-4 text-sm font-bold text-slate-800 hover:bg-slate-50"
                >
                  {es ? 'Volver al proyecto' : 'Back to the project'}
                </Link>
              )}
            </div>
          </div>
        );
      })}
    </div>
  );
};

export default AwaitingPaymentNotice;
